// Matching shared by the two US-state pickers built on app/config/us_states.json
// (@us-states): signup's patient state (visits/components/autocompleteUSStates)
// and the shipping address (shared/widgets/autocompleteUSStates). Their option
// shapes differ ({ value, label } vs { abbreviation, name }), so callers pass
// accessors.

const defaultAccessors = {
  getCode: (option) => option.value,
  getLabel: (option) => option.label,
};

// Letters only, lower-cased: "South Carolina", "south  carolina" and the
// checkout picker's space-stripped "southcarolina" all compare equal.
const letters = (text) => (text || "").toLowerCase().replace(/[^a-z]/g, "");

// The state name without its " (XX)" suffix: "South Carolina  (SC)" ->
// "South Carolina".
const stateName = (label) => label.replace(/\s*\([A-Za-z]{2}\)\s*$/, "");

// The option the input names exactly, or undefined: a two-letter code ("in" ->
// Indiana), a full name ("south carolina", "VIRGINIA" -> Virginia, not West
// Virginia), or the whole label ("South Carolina (SC)"). Case, spaces and
// punctuation are ignored.
export const findExactStateMatch = (
  options,
  inputValue,
  { getCode, getLabel } = defaultAccessors,
) => {
  const code = (inputValue || "").trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(code)) {
    const byCode = options.find((option) => getCode(option) === code);
    if (byCode) {
      return byCode;
    }
  }
  const typed = letters(inputValue);
  if (typed.length <= 2) {
    return undefined;
  }
  return options.find(
    (option) =>
      letters(stateName(getLabel(option))) === typed ||
      letters(getLabel(option)) === typed,
  );
};

// Options whose name contains the input (ignoring case, spaces and
// punctuation) or whose label contains it as typed, with an exact match (see
// findExactStateMatch) moved to the top: "IN" puts Indiana first, not Illinois.
export const filterStateOptions = (
  options,
  inputValue,
  accessors = defaultAccessors,
) => {
  const { getLabel } = accessors;
  const raw = (inputValue || "").toLowerCase();
  const typed = letters(inputValue);
  const matches = options.filter(
    (option) =>
      getLabel(option).toLowerCase().includes(raw) ||
      letters(stateName(getLabel(option))).includes(typed),
  );
  const exact = findExactStateMatch(options, inputValue, accessors);
  return exact ? [exact, ...matches.filter((o) => o !== exact)] : matches;
};
